import DeliveryAddressForm from '@components/DeliveryAddressForm';
import { Grid, IconButton, makeStyles, Typography } from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { transformDeliveryAddressToAddress } from 'src/hook-form-helper';
import useRole from 'src/hooks/useRole';
import useSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import { useGetSelectedTransportOption, useSetTransportInfo } from 'src/hooks/useTransportOptions';
import { getLabel, transportDeliveryStatus, transportOptionProvider, transportOptionStatus } from 'src/modules/labels';
import UserRole from 'src/types/UserRoles';

import {
  BiddingDeliveryAddressSelectionDTO,
  DeliveryAddress,
  SetTransportationDTO,
  SourceType,
  TransportationProvider,
  TransportationState,
} from '../modules/generated/api';
import { setToArray } from '../modules/util';

import { SourceAddressForm } from './SourceAddressForm';
import UnitValue from './UnitValue';

type DeliveryAddressProps = {
  deliveryAddressSelection?: BiddingDeliveryAddressSelectionDTO;
  bidId?: string;
  vin?: string;
  source?: SourceType;
};

const useStyles = makeStyles((theme) => ({
  displayAddress: {
    '& p': {
      margin: theme.spacing(0.5),
    },
  },
  heading: {
    display: 'flex',
    alignItems: 'center',
    '& button': {
      padding: '10px',
    },
    paddingBottom: theme.spacing(2),
  },
  subHeading: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    fontVariant: 'small-caps',
  },
  tiles: {
    padding: theme.spacing(1),
  },
  editDeliveryAddress: {
    marginTop: theme.spacing(3),
  },
  SourceAddressForm: {
    marginTop: theme.spacing(1),
  },
  noTransportDiv: {
    marginTop: theme.spacing(1),
  },
  printHidden: {
    '@media print': {
      display: 'none',
    },
  },
}));

export type BoughtCarDeliveryAddressForm = {
  deliveryAddressId: string | undefined;
  transportOptionId: string | undefined;
};

const BoughtCarDeliveryAddressTransportation = ({
  deliveryAddressSelection,
  bidId,
  vin,
  source,
}: DeliveryAddressProps) => {
  const { showError, showSuccess } = useSnackbarGlobal();
  const { mutate: postTransportId, isLoading } = useSetTransportInfo(bidId || '');
  const { data: transportInfo, refetch, isLoading: loadingTransportInfo } = useGetSelectedTransportOption(bidId || '');
  const {
    deliveryAddress,
    status,
    option,
    sourceAddress,
    deliveryStatus,
    deliveryAddressChangeable,
    otherTransportOptionsAvailable,
  } = transportInfo || {};

  const { t } = useTranslation();
  const classes = useStyles();
  const { hasRole } = useRole();
  const isAdmin = hasRole(UserRole.ADMIN);

  // transport options only ever exist for VWFS sources (see src/containers/BidBuy.tsx) - other
  // sources always get an empty options list from the backend, so this screen can't apply a
  // transport option (or a delivery address, which is only persisted as part of applying one) for them.
  const isVwfs = source === SourceType.Vwfs;
  const showFullTransportInfo =
    isVwfs &&
    option?.id &&
    sourceAddress != null &&
    (option?.provider !== TransportationProvider.PhsTransshipment || isAdmin);
  const hideTransportSelection = Boolean(
    option?.id && option?.provider === TransportationProvider.PhsTransshipment && !isAdmin,
  );

  const [editTransportOptions, setEditTransportOptions] = useState(false);

  const methods = useForm<BoughtCarDeliveryAddressForm>({
    defaultValues: { deliveryAddressId: deliveryAddress?.id, transportOptionId: option?.id },
  });
  const { reset, getValues } = methods;
  const transportEditable = isVwfs && (deliveryAddressChangeable || otherTransportOptionsAvailable);
  const sourceAddressEditable = status === TransportationState.WaitingForValidSourceAddress;

  const selectableDeliveryAddresses = setToArray(
    deliveryAddressSelection?.deliveryAddresses || (new Set() as Set<DeliveryAddress>),
  );

  const handleCancel = () => {
    reset();
    setEditTransportOptions(false);
  };

  const handleSubmit = () => {
    const optionId = getValues('transportOptionId');
    if (!optionId) {
      return;
    }
    const transportation: SetTransportationDTO = {
      transportOptionId: optionId,
    };
    postTransportId(transportation, {
      onSuccess: () => {
        setEditTransportOptions(false);
        showSuccess(t('snackBar.success.update'));
        refetch();
      },
      onError: () => {
        showError(t('snackBar.error.update'));
      },
    });
  };

  useEffect(() => {
    reset({ deliveryAddressId: deliveryAddress?.id, transportOptionId: option?.id });
  }, [reset, deliveryAddress, option]);

  if (!bidId)
    return (
      <div className={classes.tiles}>
        <Typography variant="h2">{t('configuration.deliveryAndTransport')}</Typography>
        <Typography>{t('configuration.noInfoAvailable')}</Typography>
      </div>
    );

  return (
    <div className={classes.tiles}>
      <div className={classes.heading}>
        <Typography variant="h2" id="delivery-and-transport">
          {t('configuration.deliveryAndTransport')}{' '}
        </Typography>
      </div>

      <Grid container alignItems="center" spacing={1}>
        <Grid item>
          <Typography className={classes.subHeading}>
            {t('configuration.deliveryAddress')}
            {showFullTransportInfo && <> & {t('configuration.transportOption')}</>}
          </Typography>
        </Grid>
        <Grid item>
          {transportEditable && !editTransportOptions && (
            <IconButton size="small" onClick={() => setEditTransportOptions(true)}>
              <Edit />
            </IconButton>
          )}
        </Grid>
      </Grid>
      {!editTransportOptions && deliveryAddress && (
        <div className={classes.displayAddress}>
          <p>
            {[deliveryAddress.companyName, deliveryAddress.betriebsId, deliveryAddress.addressCode]
              .filter(Boolean)
              .join(', ')}
          </p>
          <p>{deliveryAddress.street}</p>
          <p>
            {deliveryAddress.zip} {deliveryAddress.city}
          </p>
          <p>{deliveryAddress.country}</p>

          <p>
            <strong>{t('configuration.contactPerson')}: </strong>
            {deliveryAddress.contactPersonName}
          </p>
          <p>
            <strong>{t('contact.phone')}: </strong>
            {deliveryAddress.phoneNumber}
          </p>
          <p>
            <strong>{t('contact.E-Mail')}: </strong>
            {deliveryAddress.email}
          </p>
          {deliveryAddress.additionalInformation && (
            <p>
              <strong>{t('contact.additionalInformation')}: </strong>
              {deliveryAddress.additionalInformation}
            </p>
          )}
        </div>
      )}
      {editTransportOptions && (
        <DeliveryAddressForm
          deliveryAddresses={selectableDeliveryAddresses}
          methods={methods}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          showLink
          originTransportationId={option?.id}
          bidId={bidId}
          onlyDelivery={hideTransportSelection}
        />
      )}
      {!editTransportOptions && showFullTransportInfo && (
        <div className={classes.displayAddress}>
          {option ? (
            <>
              <br />
              <p>
                <strong>{t('configuration.transportOption')}: </strong>
                {getLabel(transportOptionProvider, option?.provider || '')}
              </p>
              {option.cost && (
                <p>
                  <strong>{t('configuration.costs')}: </strong>
                  <UnitValue
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    value={option.cost.amount}
                    unit={option.cost.currency}
                  />
                </p>
              )}
              <p>
                <strong>{t('configuration.status')}: </strong>
                {getLabel(transportOptionStatus, status || '')}
              </p>
              {deliveryStatus && (
                <p>
                  <strong>{t('transportation.deliveryStatusLabel')}: </strong>
                  {getLabel(transportDeliveryStatus, deliveryStatus || '')}
                </p>
              )}
            </>
          ) : (
            <div className={classes.noTransportDiv}>
              <strong>{t('configuration.transportOption')}: </strong>
              <p>{t('configuration.noInfoAvailable')}</p>
            </div>
          )}
        </div>
      )}
      <br />
      {!editTransportOptions && showFullTransportInfo && (
        <section>
          <Grid container alignItems="center" spacing={1} id="sourceAddress">
            <Grid item>
              <Typography className={classes.subHeading}>{t('configuration.sourceAddress')}</Typography>
            </Grid>
          </Grid>
          {!sourceAddressEditable && (
            <div className={classes.displayAddress}>
              {status === TransportationState.WaitingForValidSourceAddressFromVwfs && (
                <Alert severity="info" variant="outlined" className={classes.editDeliveryAddress}>
                  {t('transportation.waitingForValidSourceAddressFromVwfs-info')}
                </Alert>
              )}
              {sourceAddress && status !== TransportationState.WaitingForValidSourceAddressFromVwfs && (
                <>
                  <p>{sourceAddress.companyName}</p>
                  <p>{sourceAddress.street}</p>
                  <p>
                    {sourceAddress.zip} {sourceAddress.city}
                  </p>
                  <p>{sourceAddress.country}</p>

                  <p>
                    <strong>{t('configuration.contactPerson')}: </strong>
                    {sourceAddress.contactPersonName}
                  </p>
                  <p>
                    <strong>{t('contact.phone')}: </strong>
                    {sourceAddress.phoneNumber}
                  </p>
                  <p>
                    <strong>{t('contact.E-Mail')}: </strong>
                    {sourceAddress.email}
                  </p>
                </>
              )}
              {!sourceAddress && status !== TransportationState.WaitingForValidSourceAddressFromVwfs && (
                <p>{t('configuration.noInfoAvailable')}</p>
              )}
            </div>
          )}
          {sourceAddressEditable && !loadingTransportInfo && (
            <div className={clsx(classes.SourceAddressForm, classes.printHidden)}>
              <SourceAddressForm
                bidId={bidId}
                sourceAddress={transformDeliveryAddressToAddress(sourceAddress)}
                refetch={refetch}
                vin={vin}
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default BoughtCarDeliveryAddressTransportation;
